import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentUser, signupParentStep2 } from '../../actions/action_auth';
import { getParentData, getStudents } from '../../actions/action_parent';
import { safeRedirectTo } from '../../utils/auth';
import { COUNTRIES } from '../../constants/countries';

// React.js components
import Dropdown from '../utils/dropdown';

// Full state names sorted alphabetically, submitting the two-letter code --
// the conventional pattern for a US state select. Sort key must stay the
// displayed label: ordering by code while showing names (or vice versa)
// reads as unsorted on screen.
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

function ParentSignupFormStep2(props) {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const nonUsInternationalizationEnabled = useSelector(
    (state) => state.company.nonUsInternationalizationEnabled,
  );

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [stateValue, setStateValue] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [country, setCountry] = useState('US');
  const [errors, setErrors] = useState({});

  const isUs = country === 'US';

  const handleStateChange = (event) => setStateValue(event.target.value);
  const handleCountryChange = (event) => {
    setCountry(event.target.value);
    setStateValue('');
  };

  const handleOnSubmit = (event) => {
    event.preventDefault();
    const data = {
      first_name: firstName,
      last_name: lastName,
      phone,
      address: {
        address_1: address1,
        address_2: address2,
        city,
        state: stateValue,
        zipcode,
        country,
      },
    };

    setErrors({});
    dispatch(signupParentStep2(data)).then((response) => {
      if (response.payload.data.errors) {
        setErrors(response.payload.data.errors);
      } else {
        dispatch(getParentData());
        dispatch(setCurrentUser(auth.authToken));
        props.history.push(safeRedirectTo(props.redirectTo));
      }
    });
  };

  const firstNameErrorClassName = errors.firstName ? 'error--red' : 'hidden';
  const lastNameErrorClassName = errors.lastName ? 'error--red' : 'hidden';
  const phoneErrorClassName = errors.phone ? 'error--red' : 'hidden';
  const address1ErrorClassName = errors.address1 ? 'error--red' : 'hidden';
  const cityErrorClassName = errors.city ? 'error--red' : 'hidden';
  const stateErrorClassName = errors.state ? 'error--red' : 'hidden';
  const zipcodeErrorClassName = errors.zipcode ? 'error--red' : 'hidden';
  const bgClass = 'bg--bnl-light-blue';

  return (
    <div>
      <div className="signup__header">Create Your Account</div>
      <div className="signup__subheader">
        Please enter parent details. We value your privacy and won’t sell or
        repurpose your data in any way.
      </div>
      <form onSubmit={handleOnSubmit}>
        <input
          className="signup-form__input border--round"
          onChange={(e) => setFirstName(e.target.value)}
          name="firstName"
          value={firstName}
          placeholder="First Name"
        />
        <div className={`signup-error ${firstNameErrorClassName}`}>
          {errors.firstName}
        </div>
        <input
          className="signup-form__input border--round"
          onChange={(e) => setLastName(e.target.value)}
          name="lastName"
          value={lastName}
          placeholder="Last Name"
        />
        <div className={`signup-error ${lastNameErrorClassName}`}>
          {errors.lastName}
        </div>
        <input
          className="signup-form__input border--round"
          onChange={(e) => setPhone(e.target.value)}
          name="phone"
          value={phone}
          placeholder="Mobile Phone Number"
        />
        <div className={`signup-error ${phoneErrorClassName}`}>
          {errors.phone}
        </div>
        {nonUsInternationalizationEnabled && (
          <select
            className="signup-form__input border--round"
            onChange={handleCountryChange}
            name="country"
            value={country}
          >
            {COUNTRIES.map(({ code, name }) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        )}
        <input
          className="signup-form__input border--round"
          onChange={(e) => setAddress1(e.target.value)}
          name="address1"
          value={address1}
          placeholder="Address 1"
        />
        <div className={`signup-error ${address1ErrorClassName}`}>
          {errors.address1}
        </div>
        <input
          className="signup-form__input border--round"
          onChange={(e) => setAddress2(e.target.value)}
          name="address2"
          value={address2}
          placeholder="Address 2"
        />
        <input
          className="signup-form__input border--round"
          onChange={(e) => setCity(e.target.value)}
          name="city"
          value={city}
          placeholder="City"
        />
        <div className={`signup-error ${cityErrorClassName}`}>
          {errors.city}
        </div>
        {isUs ? (
          <Dropdown
            className="signup-form__input border--round"
            handleOnChange={handleStateChange}
            name="state"
            options={US_STATES}
            placeholder="State"
            value={stateValue}
          />
        ) : (
          <input
            className="signup-form__input border--round"
            onChange={handleStateChange}
            name="state"
            value={stateValue}
            placeholder="State / Province / Region"
          />
        )}
        <div className={`signup-error ${stateErrorClassName}`}>
          {errors.state}
        </div>
        <input
          className="signup-form__input border--round"
          onChange={(e) => setZipcode(e.target.value)}
          name="zipcode"
          value={zipcode}
          placeholder={isUs ? 'Zipcode' : 'Postal code'}
        />
        <div className={`signup-error ${zipcodeErrorClassName}`}>
          {errors.zipcode}
        </div>
        <input
          className={`display--block button--round-border ${bgClass}`}
          value="Done"
          type="submit"
        />
        <div className="signup-form__agreement">
          By creating your account, you agree to the{' '}
          <a href="/terms" target="_blank">
            Terms & Conditions
          </a>{' '}
          and{' '}
          <a href="/privacy" target="_blank">
            Privacy Policy
          </a>
        </div>
      </form>
    </div>
  );
}

export default ParentSignupFormStep2;
